/**
 * The table's colours. The canvas cannot read CSS custom properties, so the
 * palette lives here, and `styles.css` repeats the handful the HTML layer needs
 * (the accent, the parchment, the ink) under the same names.
 *
 * One look in both colour schemes: the table is a physical scene — stone, wood
 * and paper — and a board does not change colour when the lights go down.
 */
import type { Color } from '@trajan/engine';

export const INK = 0x2b2016;
export const INK_SOFT = 0x6b5a45;
export const INK_FAINT = 0x9a8a72;
export const ENGRAVE_LIGHT = 0xfff8ea;
export const ACCENT = 0x8b2e2e;
export const ACCENT_LIGHT = 0xc05a48;

export const BRONZE = 0xa8763a;
export const BRONZE_LIGHT = 0xe0b268;
export const BRONZE_DARK = 0x5e3f1a;
export const GOLD = 0xf2c75c;

/** Live targets breathe in this; a picked one is ringed in the pale version. */
export const LIVE = 0xffc94d;
export const PICKED = 0xfff0b8;

/** Surfaces, for flat fills and for the noise textures' two ends. */
export const WOOD = { dark: 0x1f130b, mid: 0x3a2415, light: 0x5c3a22 } as const;
export const STONE = { light: 0xf1e8d6, mid: 0xe0d2b6, dark: 0xc3ae88 } as const;
export const PARCHMENT = { light: 0xefe1bd, mid: 0xdfc995, dark: 0xb89a64 } as const;
export const WATER = { light: 0x5f8f96, mid: 0x3f6d78, dark: 0x274a57 } as const;
export const CARD = { face: 0xf7efdc, edge: 0xcbb993 } as const;

/** The recessed areas of the board, a shade darker than the slab. */
export const RECESS = 0xd6c6a6;

export interface Paint {
  base: number;
  light: number;
  dark: number;
}

/** Seat colours, as painted wood: Red, Green, Blue, Brown — the names the hot seat uses. */
export const SEAT: readonly Paint[] = [
  { base: 0xb03a2e, light: 0xde6a55, dark: 0x6a1f18 },
  { base: 0x4c8a3b, light: 0x7dbb62, dark: 0x274f1f },
  { base: 0x2f5ea8, light: 0x6390d8, dark: 0x183463 },
  { base: 0x8a5a2c, light: 0xbd8a55, dark: 0x4a2f14 },
];

export const seatPaint = (seat: number): Paint => SEAT[seat] ?? SEAT[0]!;

/** The six action-marker colours, each also carrying its own glyph (see pieces.ts). */
export const MARKER_PAINT: Record<Color, Paint> = {
  yellow: { base: 0xe9c440, light: 0xfbe488, dark: 0x9c7d18 },
  orange: { base: 0xe0782f, light: 0xf6a867, dark: 0x93461a },
  green: { base: 0x6aa84c, light: 0x9fd07e, dark: 0x3b6a27 },
  white: { base: 0xefeadc, light: 0xffffff, dark: 0xa9a28f },
  pink: { base: 0xe28bb0, light: 0xf6bdd4, dark: 0x9c4f71 },
  blue: { base: 0x4a7bd0, light: 0x86aaea, dark: 0x264a8a },
};

/** Tile faces by what they are, so a glance sorts the forum. */
export const TILE = {
  demand: { base: 0xe8d3a8, light: 0xf8ead0, dark: 0xa98a57 },
  senate: { base: 0xcfb8d8, light: 0xe8daee, dark: 0x7e6490 },
  wild: { base: 0xa9c7c2, light: 0xd3e6e2, dark: 0x5c7f7a },
  extra: { base: 0xe9c555, light: 0xfbe39a, dark: 0x9d7a1c },
  building: { base: 0xc98a5c, light: 0xe8b48c, dark: 0x7d4c2c },
  trajan: { base: 0xeee0c0, light: 0xfff6e0, dark: 0xa89468 },
  bonusYellow: { base: 0xefd06a, light: 0xfbe9a8, dark: 0xa4852a },
  bonusGrey: { base: 0xc9c5ba, light: 0xe8e5de, dark: 0x85807a },
} as const satisfies Record<string, Paint>;

/** A colour as a CSS string, for the few places the HTML needs one of these. */
export const css = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

/** Mix two colours; `t` = 0 gives `a`, 1 gives `b`. */
export function mix(a: number, b: number, t: number): number {
  const ch = (c: number, s: number) => (c >> s) & 0xff;
  const m = (s: number) => Math.round(ch(a, s) + (ch(b, s) - ch(a, s)) * t);
  return (m(16) << 16) | (m(8) << 8) | m(0);
}
